import type { FC } from 'react'
import {
  alpha,
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import { ArrowLeftOutlined, FileTextOutlined } from '@ant-design/icons'
import { motion } from 'framer-motion'
import type { ImportOption } from '../types'

interface SmartImportOptionsProps {
  file: File
  options: ImportOption[]
  selectedOption: ImportOption['id'] | null
  onSelect: (optionId: ImportOption['id']) => void
  onBack: () => void
  isLoading: boolean
}

const SmartImportOptions: FC<SmartImportOptionsProps> = ({
  file,
  options,
  selectedOption,
  onSelect,
  onBack,
  isLoading,
}) => {
  const theme = useTheme()

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.1 + i * 0.1,
        duration: 0.4,
        ease: [0.25, 0.46, 0.45, 0.94] as const,
      },
    }),
  }

  const spreadsheetMimeTypes = new Set([
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ])

  const fileName = file.name.toLowerCase()
  const isSpreadsheetByExtension = fileName.endsWith('.csv') || fileName.endsWith('.xls') || fileName.endsWith('.xlsx')
  const isSpreadsheetByMime = spreadsheetMimeTypes.has(file.type)
  const isSpreadsheetFile = isSpreadsheetByExtension || isSpreadsheetByMime

  return (
    <Stack spacing={4} alignItems="center">
      {/* Header with file info */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        style={{ width: '100%' }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={onBack}
            size="small"
            sx={{
              color: theme.palette.text.secondary,
              '&:hover': {
                color: theme.palette.text.primary,
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <ArrowLeftOutlined />
          </IconButton>

          <Box
            sx={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              backgroundColor: alpha(theme.palette.background.paper, 0.8),
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: 1.5,
                backgroundColor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            >
              <FileTextOutlined style={{ fontSize: 18 }} />
            </Box>
            <Stack spacing={0}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(file.size)}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </motion.div>

      {/* Question */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15, duration: 0.4 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            textAlign: 'center',
            color: theme.palette.text.primary,
          }}
        >
          What would you like to import?
        </Typography>
      </motion.div>

      {/* Option cards */}
      <Stack spacing={2} sx={{ width: '100%' }}>
        {options.map((option, index) => {
          const isSelected = selectedOption === option.id
          const isEmployeeRewardPackage = option.id === 'employee-reward-package'
          const isUnsupportedEmployeeFile = isEmployeeRewardPackage && !isSpreadsheetFile
          const isDisabled = (isLoading && !isSelected) || isUnsupportedEmployeeFile
          const disabledReason = isUnsupportedEmployeeFile
            ? 'Only CSV and Excel files are supported for this smart import.'
            : ''

          return (
            <motion.div
              key={option.id}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              animate="visible"
              whileHover={isDisabled ? undefined : { y: -2 }}
              transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
              style={{ cursor: isUnsupportedEmployeeFile ? 'not-allowed' : isDisabled ? 'default' : 'pointer' }}
            >
              <Tooltip title={disabledReason} arrow disableHoverListener={!isUnsupportedEmployeeFile}>
                <Box component="span" sx={{ display: 'block', width: '100%' }}>
                  <ButtonBase
                    onClick={() => !isDisabled && onSelect(option.id)}
                    disabled={isDisabled}
                    sx={{
                      width: '100%',
                      textAlign: 'left',
                      borderRadius: 3,
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2.5,
                        p: 3,
                        borderRadius: 3,
                        border: `1px solid ${isSelected ? option.color : theme.palette.divider}`,
                        backgroundColor: isSelected ? alpha(option.color, 0.04) : theme.palette.background.paper,
                        opacity: isDisabled ? 0.5 : 1,
                        transition: 'all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                        '&:hover': isDisabled
                          ? {}
                          : {
                              borderColor: option.color,
                              backgroundColor: alpha(option.color, 0.03),
                              boxShadow: `0 8px 24px ${alpha(option.color, 0.12)}`,
                            },
                      }}
                    >
                      {/* Icon */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 52,
                          height: 52,
                          borderRadius: 2.5,
                          backgroundColor: alpha(option.color, 0.1),
                          color: option.color,
                          flexShrink: 0,
                          transition: 'all 0.25s ease',
                        }}
                      >
                        {isSelected && isLoading ? (
                          <CircularProgress size={24} sx={{ color: option.color }} />
                        ) : (
                          option.icon
                        )}
                      </Box>

                      {/* Text */}
                      <Stack spacing={0.5} sx={{ flex: 1 }}>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            fontWeight: 600,
                            color: isSelected ? option.color : theme.palette.text.primary,
                            transition: 'color 0.25s ease',
                          }}
                        >
                          {option.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.secondary,
                            lineHeight: 1.5,
                          }}
                        >
                          {option.description}
                        </Typography>
                      </Stack>

                      {/* Selection indicator */}
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: '50%',
                          border: `2px solid ${isSelected ? option.color : theme.palette.divider}`,
                          backgroundColor: isSelected ? option.color : 'transparent',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.25s ease',
                          flexShrink: 0,
                        }}
                      >
                        {isSelected && (
                          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ duration: 0.2 }}>
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: theme.palette.common.white,
                              }}
                            />
                          </motion.div>
                        )}
                      </Box>
                    </Box>
                  </ButtonBase>
                </Box>
              </Tooltip>
            </motion.div>
          )
        })}
      </Stack>
    </Stack>
  )
}

export default SmartImportOptions
